import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, Save, Loader2, AlertCircle, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Admin: list all LMS quizzes across both courses, show progress on
 * authoring (how many questions still need answers entered).
 */
export function AdminQuizList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user === false || (user && user.role !== "admin")) navigate("/");
  }, [user, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/lms/quizzes");
        setQuizzes(data);
      } catch (e) {
        toast.error("Could not load quizzes");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="pt-36 container-aviana"><Loader2 className="animate-spin text-aviana-gold" /></div>;

  // Group by course
  const byCourse = quizzes.reduce((acc, q) => {
    const key = q.course_title || "Unassigned";
    (acc[key] ||= []).push(q);
    return acc;
  }, {});

  return (
    <div className="pt-36 pb-24" data-testid="admin-quiz-list">
      <div className="container-aviana max-w-5xl">
        <Link to="/admin" className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8" data-testid="back-to-admin">
          <ArrowLeft size={14} /> Back to admin
        </Link>
        <p className="overline-plum mb-5">Course authoring</p>
        <h1 className="font-display text-5xl md:text-6xl text-aviana-ink leading-[1.0]">
          Quiz <em className="italic font-serif gold-text">authoring.</em>
        </h1>
        <p className="mt-7 font-serif italic text-aviana-muted text-[18px] leading-[1.6] max-w-2xl">
          Your LearnDash questions are imported — but WPProQuiz answer data didn't transfer with the WordPress export. Click each quiz to fill in the answer choices and mark which is correct.
        </p>

        <div className="mt-14 space-y-12">
          {Object.entries(byCourse).map(([courseTitle, list]) => (
            <div key={courseTitle}>
              <h2 className="font-display text-2xl text-aviana-plum mb-5">{courseTitle}</h2>
              <div className="space-y-3">
                {list.map((q) => {
                  const pct = q.question_count > 0 ? Math.round((q.authored_count / q.question_count) * 100) : 0;
                  const done = q.authored_count === q.question_count && q.question_count > 0;
                  return (
                    <Link
                      key={q.id}
                      to={`/admin/lms/quiz/${q.id}`}
                      className="block bg-aviana-cream rounded-2xl p-6 border border-aviana-border hover:border-aviana-gold/60 transition-all group"
                      data-testid={`quiz-card-${q.id}`}
                    >
                      <div className="flex items-center justify-between gap-6">
                        <div className="flex-1">
                          <p className="overline-plum text-[10px] mb-2">{q.lesson_title}</p>
                          <h3 className="font-display text-2xl text-aviana-ink group-hover:text-aviana-plum transition-colors">{q.title}</h3>
                          <p className="mt-2 text-sm text-aviana-muted">{q.question_count} questions · {q.authored_count} authored</p>
                        </div>
                        <div className="flex items-center gap-5 shrink-0">
                          <div className="w-32 h-2 rounded-full bg-aviana-border overflow-hidden">
                            <div className="h-full bg-aviana-gold transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className={`text-[11px] tracking-[0.18em] uppercase ${done ? "text-aviana-gold" : "text-aviana-muted"}`}>
                            {done ? <Check className="inline" size={14} /> : `${pct}%`}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Admin: edit one quiz's questions — fill in answer choices + mark correct.
 * Auto-saves on blur. Inline preview of question_html.
 *
 * Draft persistence: every edit is mirrored to localStorage keyed by question_id,
 * so a session timeout / refresh never loses unsaved typing.
 */
const DRAFT_KEY = (qid) => `aviana_quiz_draft_${qid}`;

export function AdminQuizEditor() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [dirty, setDirty] = useState({});  // question_id -> true if has unsaved edits

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/lms/quiz/${quizId}/questions`);
        // Restore drafts + ensure every question has at least 4 choices
        for (const q of data.questions) {
          const draftStr = typeof window !== "undefined" ? localStorage.getItem(DRAFT_KEY(q.id)) : null;
          if (draftStr) {
            try {
              const draft = JSON.parse(draftStr);
              Object.assign(q, draft);
            } catch {}
          }
          if (!q.choices || q.choices.length === 0) {
            q.choices = [{ text: "", correct: false }, { text: "", correct: false }, { text: "", correct: false }, { text: "", correct: false }];
          }
        }
        setData(data);
      } catch (e) {
        if (e.response?.status !== 401) toast.error("Could not load quiz");
        // 401 is already handled by the global interceptor (redirects to /login)
      } finally { setLoading(false); }
    })();
  }, [quizId]);

  // Save a draft snapshot for one question to localStorage (debounced via setTimeout)
  const stashDraft = (q) => {
    try { localStorage.setItem(DRAFT_KEY(q.id), JSON.stringify({
      question_html: q.question_html, choices: q.choices,
      points: q.points, question_type: q.question_type,
    })); } catch {}
  };

  const mutateQuestion = (qIdx, transform) => {
    setData((d) => {
      const next = { ...d, questions: [...d.questions] };
      next.questions[qIdx] = transform(next.questions[qIdx]);
      stashDraft(next.questions[qIdx]);
      setDirty((dr) => ({ ...dr, [next.questions[qIdx].id]: true }));
      return next;
    });
  };

  const updateChoice = (qIdx, cIdx, key, val) =>
    mutateQuestion(qIdx, (q) => {
      const choices = [...q.choices];
      choices[cIdx] = { ...choices[cIdx], [key]: val };
      return { ...q, choices };
    });

  const markCorrect = (qIdx, cIdx) =>
    mutateQuestion(qIdx, (q) => {
      const isMultiple = q.question_type === "multiple";
      const choices = q.choices.map((c, i) => ({
        ...c,
        correct: isMultiple ? (i === cIdx ? !c.correct : c.correct) : (i === cIdx),
      }));
      return { ...q, choices };
    });

  const addChoice = (qIdx) =>
    mutateQuestion(qIdx, (q) => ({ ...q, choices: [...q.choices, { text: "", correct: false }] }));

  const removeChoice = (qIdx, cIdx) =>
    mutateQuestion(qIdx, (q) => ({ ...q, choices: q.choices.filter((_, i) => i !== cIdx) }));

  const saveQuestion = async (q) => {
    setSavingId(q.id);
    try {
      await api.put(`/admin/lms/question/${q.id}`, {
        question_html: q.question_html,
        choices: (q.question_type === "essay" || q.question_type === "survey")
          ? []  // these types don't have scored choices
          : q.choices.filter((c) => c.text.trim()),
        explanation: q.explanation || null,
        points: q.points || 1,
        question_type: q.question_type || "single",
      });
      // Clear draft + dirty flag for this question
      localStorage.removeItem(DRAFT_KEY(q.id));
      setDirty((dr) => { const n = { ...dr }; delete n[q.id]; return n; });
      toast.success("Saved");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Save failed";
      // 401 is handled globally — show a more helpful message for other failures
      if (e.response?.status !== 401) {
        toast.error(`Save failed: ${formatApiError(msg)} — your typing is safe in this browser`);
      }
    } finally { setSavingId(null); }
  };

  const deleteQuestion = async (q) => {
    if (!window.confirm(`Delete this question permanently?\n\n"${(q.question_html||q.title||"").replace(/<[^>]*>/g, "").substring(0,120)}"`)) return;
    try {
      await api.delete(`/admin/lms/question/${q.id}`);
      localStorage.removeItem(DRAFT_KEY(q.id));
      setData((d) => ({ ...d, questions: d.questions.filter((x) => x.id !== q.id) }));
      setDirty((dr) => { const n = { ...dr }; delete n[q.id]; return n; });
      toast.success("Question deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not delete");
    }
  };

  if (loading) return <div className="pt-36 container-aviana"><Loader2 className="animate-spin text-aviana-gold" /></div>;
  if (!data) return (
    <div className="pt-36 pb-24 container-aviana text-center">
      <p className="text-aviana-muted">Could not load this quiz. Please go back and try again.</p>
      <Link to="/admin/lms/quizzes" className="btn-secondary mt-6 inline-flex">← All quizzes</Link>
    </div>
  );

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="pt-36 pb-32" data-testid="admin-quiz-editor">
      <div className="container-aviana max-w-4xl">
        <Link to="/admin/lms/quizzes" className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase text-aviana-muted hover:text-aviana-ink mb-8">
          <ArrowLeft size={14} /> All quizzes
        </Link>
        <p className="overline-plum mb-4">Editing quiz</p>
        <h1 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">{data.quiz.title}</h1>
        <p className="mt-6 text-aviana-muted text-[15px]">
          {data.questions.length} questions · For each: enter the answer choices and click the gold circle on the correct one.
        </p>
        {dirtyCount > 0 && (
          <div className="mt-5 inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-aviana-gold bg-aviana-gold/10 border border-aviana-gold/30 px-4 py-2 rounded-full" data-testid="unsaved-banner">
            <AlertCircle size={14} /> {dirtyCount} unsaved {dirtyCount === 1 ? "question" : "questions"} — drafts are safe in this browser even if you refresh.
          </div>
        )}

        {/* Bulk actions */}
        <div className="mt-7 flex flex-wrap gap-3" data-testid="bulk-actions">
          <button
            onClick={() => {
              if (!window.confirm(`Mark all ${data.questions.length} questions in this quiz as "Survey" (no right/wrong, free-form answers)?`)) return;
              setData((d) => {
                const next = { ...d, questions: d.questions.map((q) => ({ ...q, question_type: "survey" })) };
                next.questions.forEach((q) => { stashDraft(q); });
                setDirty(Object.fromEntries(next.questions.map((q) => [q.id, true])));
                return next;
              });
              toast.success("All questions set to Survey. Click 'Save all' below to persist.");
            }}
            className="text-[11px] tracking-[0.22em] uppercase border border-aviana-border rounded-full px-4 py-2 hover:border-aviana-plum hover:text-aviana-plum transition-colors"
            data-testid="bulk-set-survey"
          >
            Set all to Survey
          </button>
          <button
            onClick={async () => {
              const dirtyQs = data.questions.filter((q) => dirty[q.id]);
              if (dirtyQs.length === 0) { toast.info("Nothing to save."); return; }
              toast.info(`Saving ${dirtyQs.length} questions…`);
              let okCount = 0;
              for (const q of dirtyQs) {
                try {
                  await saveQuestion(q);
                  okCount++;
                } catch {}
              }
              toast.success(`Saved ${okCount} of ${dirtyQs.length} questions.`);
            }}
            disabled={dirtyCount === 0 || savingId}
            className="text-[11px] tracking-[0.22em] uppercase border border-aviana-gold/50 rounded-full px-4 py-2 text-aviana-gold hover:bg-aviana-gold hover:text-white transition-colors disabled:opacity-40"
            data-testid="bulk-save-all"
          >
            {dirtyCount > 0 ? `Save all ${dirtyCount}` : "Save all"}
          </button>
        </div>

        <div className="mt-12 space-y-8">
          {data.questions.map((q, qIdx) => (
            <article key={q.id} className={`bg-aviana-cream rounded-[1.5rem] p-8 border ${dirty[q.id] ? "border-aviana-gold/60" : "border-aviana-border"}`} data-testid={`question-${q.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <p className="text-[11px] tracking-[0.22em] uppercase text-aviana-gold">Q{qIdx + 1}{dirty[q.id] ? " · unsaved" : ""}</p>
                  <button
                    onClick={() => deleteQuestion(q)}
                    className="text-aviana-muted hover:text-aviana-plum opacity-50 hover:opacity-100 transition-opacity"
                    title="Delete this question"
                    data-testid={`delete-${q.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <select
                  value={q.question_type || "single"}
                  onChange={(e) => mutateQuestion(qIdx, (qq) => ({ ...qq, question_type: e.target.value }))}
                  className="bg-transparent text-[12px] uppercase tracking-[0.18em] text-aviana-muted border border-aviana-border rounded-full px-3 py-1"
                  data-testid={`type-${q.id}`}
                >
                  <option value="single">Single choice</option>
                  <option value="multiple">Multiple choice</option>
                  <option value="essay">Essay (manual grade)</option>
                  <option value="survey">Survey (no right answer)</option>
                </select>
              </div>

              {/* Question text — editable */}
              <div className="aviana-richtext text-aviana-ink mb-5" dangerouslySetInnerHTML={{ __html: q.question_html || `<p><em>(no question text — type below)</em></p>` }} />
              <textarea
                value={q.question_html || ""}
                onChange={(e) => mutateQuestion(qIdx, (qq) => ({ ...qq, question_html: e.target.value }))}
                className="w-full text-sm bg-white/60 rounded-lg p-3 border border-aviana-border font-mono"
                rows={2}
                placeholder="Question HTML"
                data-testid={`qhtml-${q.id}`}
              />

              {(q.question_type !== "essay" && q.question_type !== "survey") && (
                <>
                  <p className="overline mt-7 mb-4">Answer choices</p>
                  <div className="space-y-2">
                    {(q.choices || []).map((c, cIdx) => (
                      <div key={cIdx} className="flex items-center gap-3" data-testid={`choice-${q.id}-${cIdx}`}>
                        <button
                          onClick={() => markCorrect(qIdx, cIdx)}
                          className={`shrink-0 w-7 h-7 rounded-full border-2 transition-all flex items-center justify-center ${c.correct ? "border-aviana-gold bg-aviana-gold text-white" : "border-aviana-border hover:border-aviana-gold/60"}`}
                          title="Mark as correct"
                          data-testid={`correct-${q.id}-${cIdx}`}
                        >
                          {c.correct && <Check size={14} strokeWidth={3} />}
                        </button>
                        <input
                          value={c.text}
                          onChange={(e) => updateChoice(qIdx, cIdx, "text", e.target.value)}
                          className="flex-1 bg-white border border-aviana-border rounded-lg px-4 py-2.5 text-aviana-ink text-[15px] outline-none focus:border-aviana-gold/60"
                          placeholder={`Choice ${String.fromCharCode(65 + cIdx)}`}
                          data-testid={`choice-text-${q.id}-${cIdx}`}
                        />
                        <button
                          onClick={() => removeChoice(qIdx, cIdx)}
                          className="text-[11px] text-aviana-muted hover:text-aviana-plum px-2 py-1"
                          aria-label="Remove choice"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => addChoice(qIdx)}
                    className="mt-3 text-[11px] tracking-[0.22em] uppercase text-aviana-gold hover:text-aviana-goldDeep"
                    data-testid={`add-choice-${q.id}`}
                  >
                    + Add another choice
                  </button>
                </>
              )}

              {q.question_type === "survey" && (
                <p className="mt-6 text-sm text-aviana-muted italic">
                  Survey questions display the question text only — students give free-form responses, no right/wrong scoring. Perfect for the Teacher Evaluation form.
                </p>
              )}

              <div className="mt-7 flex items-center justify-between">
                <input
                  type="number"
                  value={q.points || 1}
                  onChange={(e) => mutateQuestion(qIdx, (qq) => ({ ...qq, points: parseInt(e.target.value) || 1 }))}
                  className="bg-white border border-aviana-border rounded-lg px-3 py-1.5 w-20 text-sm"
                  min={1}
                />
                <button
                  onClick={() => saveQuestion(q)}
                  disabled={savingId === q.id}
                  className="btn-gold !py-2 !px-5 !text-[11px] disabled:opacity-50"
                  data-testid={`save-${q.id}`}
                >
                  {savingId === q.id ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : <><Save size={14} /> Save this question</>}
                </button>
              </div>

              {q.question_type !== "essay" && q.question_type !== "survey" && !q.choices.some((c) => c.correct) && (
                <div className="mt-4 flex items-center gap-2 text-[12px] text-aviana-gold">
                  <AlertCircle size={14} /> Mark which choice is correct before saving.
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
